import { useMemo, useEffect, useRef, useCallback } from 'react';
import { WeavableProviderProps, WeavableAuthContext as WeavableAuthContextType } from '../types';
import { WeavableAuthContext } from '../hooks';
import { useTokenCache } from '../hooks/useTokenCache';
import { injectStyles, themeToStyles } from '../utils';
import stylesString from '../styles.css?inline';

/**
 * WeavableProvider - Provides authentication context to child components
 * Also injects component CSS and handles theming
 */
export function WeavableProvider({
    baseUrl,
    connectionId,
    getToken,
    onError,
    children,
    theme,
    style,
}: WeavableProviderProps) {
    // Inject CSS on mount
    useEffect(() => {
        injectStyles(stylesString);
    }, []);

    // Store callbacks in refs to keep them up-to-date without causing re-renders
    const getTokenRef = useRef(getToken);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        getTokenRef.current = getToken;
        onErrorRef.current = onError;
    }, [getToken, onError]);

    // Create stable callback wrappers that never change identity
    const stableGetToken = useCallback(async () => {
        return await getTokenRef.current();
    }, []);

    const stableOnError = useCallback((error: Error) => {
        onErrorRef.current?.(error);
    }, []);

    // Use token caching hook with stable getToken
    const getCachedToken = useTokenCache(stableGetToken);

    const contextValue: WeavableAuthContextType = useMemo(
        () => ({
            baseUrl,
            connectionId,
            getToken: getCachedToken,
            onError: stableOnError,
        }),
        [baseUrl, connectionId, getCachedToken, stableOnError],
    );

    // Combine theme and style props
    const combinedStyles = useMemo(() => {
        const themeStyles = themeToStyles(theme);
        return { ...themeStyles, ...style };
    }, [theme, style]);

    return (
        <WeavableAuthContext.Provider value={contextValue}>
            <div className="weavable-provider" style={combinedStyles}>
                {children}
            </div>
        </WeavableAuthContext.Provider>
    );
}
